import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import BoxAnimation from "../animation/BoxAnimation";
import TextAnimation from "../animation/TextAnimation";
import {
    STRIPE_CREATE_SUBSCRIPTION_SESSION_URL,
    STRIPE_CUSTOM_DEVELOPMENT_PAYMENT_LINK,
    STRIPE_PRICE_FRONTEND,
    STRIPE_PRICE_FULLSTACK,
    STRIPE_PRICE_WEBAPP,
    STRIPE_SESSION_STATUS_URL,
    toastifyConfigs,
} from "../../config/constants";

type Plan = {
    id: string;
    name: string;
    priceLabel: string;
    description: string;
    priceId: string;
    features: string[];
};

const subscriptionPlans: Plan[] = [
    {
        id: "frontend",
        name: "Front-end Care",
        priceLabel: "$10 / month",
        description: "Landing pages, UI tweaks, and ongoing front-end support.",
        priceId: STRIPE_PRICE_FRONTEND,
        features: [
            "Responsive landing page updates",
            "Component bug fixes",
            "Monthly UI polish session",
        ],
    },
    {
        id: "fullstack",
        name: "Full-stack Partner",
        priceLabel: "$30 / month",
        description: "Front-end plus back-end integrations and maintenance.",
        priceId: STRIPE_PRICE_FULLSTACK,
        features: [
            "Everything in Front-end Care",
            "API integrations & automation",
            "Performance & security checks",
        ],
    },
    {
        id: "webapps",
        name: "Web App Launchpad",
        priceLabel: "$50 / month",
        description: "Custom web app feature builds with priority support.",
        priceId: STRIPE_PRICE_WEBAPP,
        features: [
            "Everything in Full-stack Partner",
            "Feature planning sessions",
            "Priority release windows",
        ],
    },
];

export default function PaymentsPage() {
    const navigate = useNavigate();
    const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

    const startSubscriptionCheckout = async (plan: Plan) => {
        if (!plan.priceId) {
            toast.error("This plan is not configured yet. Add the Stripe price ID in your .env file.", toastifyConfigs as any);
            return;
        }

        try {
            setLoadingPlan(plan.id);
            const response = await fetch(STRIPE_CREATE_SUBSCRIPTION_SESSION_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ priceId: plan.priceId }),
            });

            if (!response.ok) {
                let message = `Unexpected status: ${response.status}`;
                try {
                    const data = await response.json();
                    if (data?.error && typeof data.error === "string") {
                        message = data.error;
                    }
                } catch {
                    // Keep the status message when the API does not return JSON.
                }
                throw new Error(message);
            }

            const data = await response.json();
            if (!data?.url || typeof data.url !== "string") {
                throw new Error("Stripe checkout URL not returned.");
            }

            window.location.href = data.url;
        } catch (error: any) {
            const message = error instanceof TypeError
                ? "The payment service could not be reached. Please try again shortly."
                : typeof error?.message === "string"
                    ? error.message
                    : "Unable to start checkout. Please try again.";
            toast.error(message, { ...(toastifyConfigs as any), toastId: "subscription-checkout-error" });
        } finally {
            setLoadingPlan(null);
        }
    };

    return (
        <main className="min-h-screen bg-primary_one-150 text-primary_one-50">
            <div className="mx-auto max-w-screen-xl px-4 py-16">
                <TextAnimation customProps={{ position: "top-to-bottom" }}>
                    <header className="mb-12 text-center">
                        <p className="mb-3 font-semibold uppercase tracking-[0.25em] text-primary_one-200">
                            Secure Stripe checkout
                        </p>
                        <h1 className="text-[48px] font-bold max-md:text-[34px]">
                            Payments &amp; Plans
                        </h1>
                        <p className="mx-auto mt-4 max-w-3xl text-[18px] text-gray-300 max-md:text-[16px]">
                            Pay for custom development work or choose a recurring support plan.
                        </p>
                    </header>
                </TextAnimation>

                <BoxAnimation>
                    <section className="mb-16 rounded-2xl border border-primary_one-100 bg-primary_one-140 p-10 shadow-xl max-md:p-7">
                        <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
                            <div>
                                <p className="mb-2 font-semibold uppercase tracking-wide text-primary_one-200">
                                    One-time or agreed payment
                                </p>
                                <h2 className="mb-3 text-[32px] font-bold max-md:text-[26px]">
                                    Custom Development Work
                                </h2>
                                <p className="max-w-2xl leading-7 text-gray-300">
                                    Enter the agreed amount in CAD on Stripe's hosted checkout page. Qouda does not
                                    receive or store your card number.
                                </p>
                            </div>
                            <a
                                href={STRIPE_CUSTOM_DEVELOPMENT_PAYMENT_LINK}
                                className="rounded-md bg-primary_one-200 px-8 py-4 text-center font-semibold uppercase tracking-wide text-white transition-colors hover:bg-red-700"
                            >
                                Pay securely
                            </a>
                        </div>
                    </section>
                </BoxAnimation>

                <section aria-labelledby="subscription-plans-heading">
                    <div className="mb-8">
                        <h2 id="subscription-plans-heading" className="text-[36px] font-bold max-md:text-[28px]">
                            Subscription Plans
                        </h2>
                        <p className="mt-3 text-gray-300">
                            Choose recurring support for ongoing development and maintenance.
                        </p>
                    </div>

                    <div className="grid gap-8 md:grid-cols-3">
                        {subscriptionPlans.map((plan) => (
                            <BoxAnimation key={plan.id}>
                                <article className="flex h-full flex-col rounded-2xl border border-primary_one-100 bg-primary_one-140 p-8 shadow-xl">
                                    <div className="mb-6">
                                        <h3 className="mb-2 text-[28px] font-semibold">{plan.name}</h3>
                                        <p className="mb-4 text-gray-300">{plan.description}</p>
                                        <p className="text-[34px] font-bold">{plan.priceLabel}</p>
                                    </div>

                                    <ul className="flex-1 space-y-3">
                                        {plan.features.map((feature) => (
                                            <li key={feature} className="flex items-start text-[16px]">
                                                <span className="mt-2 mr-3 h-2 w-2 flex-shrink-0 rounded-full bg-primary_one-200" />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        type="button"
                                        onClick={() => startSubscriptionCheckout(plan)}
                                        disabled={loadingPlan !== null}
                                        className="mt-8 rounded-md bg-primary_one-200 py-3 font-semibold uppercase tracking-wide text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {loadingPlan === plan.id ? "Redirecting..." : "Subscribe"}
                                    </button>
                                </article>
                            </BoxAnimation>
                        ))}
                    </div>
                </section>

                <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="mx-auto mt-12 block rounded-md border border-primary_one-200 px-8 py-3 uppercase tracking-wide text-primary_one-200 transition-colors hover:bg-primary_one-200 hover:text-white"
                >
                    Back to home
                </button>
            </div>
        </main>
    );
}

export function PaymentStatusPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const [status, setStatus] = useState<"checking" | "paid" | "processing" | "invalid" | "unavailable">("checking");
    const [session, setSession] = useState<any>(null);

    useEffect(() => {
        if (!sessionId) {
            setStatus("invalid");
            return;
        }

        const controller = new AbortController();
        const verifySession = async () => {
            try {
                const response = await fetch(`${STRIPE_SESSION_STATUS_URL}?session_id=${encodeURIComponent(sessionId)}`, {
                    signal: controller.signal,
                    headers: {
                        Accept: "application/json",
                    },
                });

                if (!response.ok) {
                    setStatus(response.status === 400 || response.status === 404 ? "invalid" : "unavailable");
                    return;
                }

                const data = await response.json();
                setSession(data);
                setStatus(data.paymentStatus === "paid" || data.paymentStatus === "no_payment_required" ? "paid" : "processing");
            } catch {
                if (!controller.signal.aborted) {
                    setStatus("unavailable");
                }
            }
        };

        verifySession();
        return () => controller.abort();
    }, [sessionId]);

    const amount = useMemo(() => {
        if (session?.amountTotal == null || !session.currency) return null;
        try {
            return new Intl.NumberFormat("en-CA", {
                style: "currency",
                currency: session.currency.toUpperCase(),
            }).format(session.amountTotal / 100);
        } catch {
            return null;
        }
    }, [session]);

    const content = {
        checking: {
            title: "Verifying Payment",
            message: "We are confirming the Checkout Session directly with Stripe.",
        },
        paid: {
            title: "Payment Confirmed",
            message: amount ? `Stripe confirmed your ${amount} payment.` : "Stripe confirmed your payment.",
        },
        processing: {
            title: "Payment Processing",
            message: "Stripe has the Checkout Session, but the payment is not marked paid yet. Some payment methods take longer to confirm.",
        },
        invalid: {
            title: "Payment Not Verified",
            message: "This page does not contain a valid Stripe Checkout Session. Please use the secure payment page or contact Qouda.",
        },
        unavailable: {
            title: "Verification Unavailable",
            message: "The payment status service could not be reached. Your Stripe receipt and the Stripe Dashboard remain the source of truth.",
        },
    }[status];

    return (
        <PaymentMessage
            title={content.title}
            message={content.message}
            primaryLabel="Back to home"
            onPrimary={() => navigate("/")}
            secondaryLabel={status !== "paid" ? "Return to payments & plans" : undefined}
            onSecondary={status !== "paid" ? () => navigate("/payments") : undefined}
        />
    );
}

export function PaymentCancelPage() {
    const navigate = useNavigate();

    return (
        <PaymentMessage
            title="Payment Not Completed"
            message="No payment confirmation was received. You can safely try again or return to the site. Failed card attempts are handled on Stripe's checkout page."
            primaryLabel="Return to payments & plans"
            onPrimary={() => navigate("/payments")}
            secondaryLabel="Back to home"
            onSecondary={() => navigate("/")}
        />
    );
}

function PaymentMessage({
    title,
    message,
    primaryLabel,
    onPrimary,
    secondaryLabel,
    onSecondary,
}: {
    title: string;
    message: string;
    primaryLabel: string;
    onPrimary: () => void;
    secondaryLabel?: string;
    onSecondary?: () => void;
}) {
    return (
        <main className="flex min-h-screen items-center justify-center bg-primary_one-150 px-4 py-16">
            <BoxAnimation>
                <section className="mx-auto max-w-2xl rounded-2xl border border-primary_one-100 bg-primary_one-140 p-10 text-center text-primary_one-50 shadow-xl">
                    <TextAnimation>
                        <h1 className="mb-4 text-[48px] font-bold max-md:text-[32px]">{title}</h1>
                    </TextAnimation>
                    <p className="mb-8 text-[18px] leading-7 text-gray-300 max-md:text-[16px]">
                        {message}
                    </p>
                    <div className="flex flex-col gap-4">
                        <button
                            type="button"
                            onClick={onPrimary}
                            className="rounded-md bg-primary_one-200 px-6 py-3 font-semibold uppercase tracking-wide text-white transition-colors hover:bg-red-700"
                        >
                            {primaryLabel}
                        </button>
                        {secondaryLabel && onSecondary && (
                            <button
                                type="button"
                                onClick={onSecondary}
                                className="rounded-md border border-primary_one-200 px-6 py-3 font-semibold uppercase tracking-wide text-primary_one-200 transition-colors hover:bg-primary_one-200 hover:text-white"
                            >
                                {secondaryLabel}
                            </button>
                        )}
                    </div>
                </section>
            </BoxAnimation>
        </main>
    );
}
